import * as React from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Grid,
  CircularProgress,
  Alert,
  Snackbar,
  Button,
  Divider,
  Card,
  CardContent,
  Stack,
  useTheme,
} from '@mui/material';
import {Zoom, Fade} from 'react-awesome-reveal';
import {createPlatformOrderPayment} from '../api/platformOrder';
import {useState, useEffect} from 'react';
import AppAppBar from '../components/AppAppBar';
import CssBaseline from '@mui/material/CssBaseline';
import {ThemeProvider, createTheme, alpha} from '@mui/material/styles';
import getLPTheme from '../components/getLPTheme';
import Footer from '../components/Footer';
import FavoriteIcon from '@mui/icons-material/Favorite';
import CodeIcon from '@mui/icons-material/Code';
import CloudIcon from '@mui/icons-material/Cloud';
import GroupsIcon from '@mui/icons-material/Groups';
import {useTranslation} from 'react-i18next';

// 擴展 Window 接口以包含 zzzpayment
declare global {
  interface Window {
    zzzpayment?: {
      init: (options: {platformId: string; baseUrl?: string}) => void;
      open: (options: {
        platformId?: string;
        amount: number | string;
        currency: string;
        showLoading?: (isLoading: boolean) => void;
        onSuccess?: (result: any) => void;
        onCancel?: () => void;
        onError?: (error: any) => void;
      }) => void;
    };
  }
}

export default function SponsorPage() {
  const [mode] = React.useState<'light' | 'dark'>('light');
  const theme = createTheme(getLPTheme(mode));
  const [amount, setAmount] = useState<number>(10);
  const [loading, setLoading] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const {t} = useTranslation();
  const themeInstance = useTheme();

  // 固定的平台ID - 這應該從環境變數或配置中獲取
  const platformId = 'dbe66e89-2edc-55b5-9c6e-820110b08d5f';

  // 加載SDK
  useEffect(() => {
    // 檢查是否已加載SDK
    if (typeof window.zzzpayment === 'undefined') {
      const script = document.createElement('script');
      script.src = 'https://zusers.zzztech.com.tw/payment-sdk.js';
      script.async = true;
      script.onload = () => {
        if (window.zzzpayment) {
          window.zzzpayment.init({
            platformId: platformId,
            baseUrl: 'https://zusers.zzztech.com.tw/checkout',
          });
          console.log('Payment SDK loaded for donation');
        }
      };
      document.body.appendChild(script);
    }
  }, [platformId]);

  const handleDonation = () => {
    if (!amount || amount < 1) {
      setError(t('sponsorInvalidAmount'));
      return;
    }

    setLoading(true);
    setError(null);

    // 使用API創建訂單並獲取支付URL
    createPlatformOrderPayment({
      platformId,
      amount: Number(amount),
      currency: 'USDT',
      redirectUrl: window.location.href,
    })
      .then(response => {
        setLoading(false);
        if (response?.data?.data?.paymentUrl) {
          // 開啟支付頁面
          window.open(response.data.data.paymentUrl, '_blank');
          setSuccess(true);

          // 3秒後重置成功狀態
          setTimeout(() => {
            setSuccess(false);
          }, 3000);
        } else {
          throw new Error(t('sponsorPaymentLinkError'));
        }
      })
      .catch(err => {
        console.error('支付錯誤:', err);
        setLoading(false);
        setError(t('sponsorPaymentError'));
      });
  };

  const usagePurposes = [
    {
      title: t('innovationResearch'),
      description: t('innovationDescription'),
      icon: <CodeIcon fontSize='large' color='primary' />,
    },
    {
      title: t('serverMaintenance'),
      description: t('serverDescription'),
      icon: <CloudIcon fontSize='large' color='primary' />,
    },
    {
      title: t('communityDevelopment'),
      description: t('communityDescription'),
      icon: <GroupsIcon fontSize='large' color='primary' />,
    },
  ];

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AppAppBar />
      <Box
        sx={{
          pt: 12,
          pb: 6,
          minHeight: 'calc(100vh - 220px)',
          bgcolor: 'background.default',
          backgroundImage: 'linear-gradient(to bottom, #f5f7fa, #e8edf5)',
        }}
      >
        <Container maxWidth='lg'>
          <Fade triggerOnce>
            <Box sx={{textAlign: 'center', mb: 6}}>
              <FavoriteIcon
                sx={{
                  fontSize: 48,
                  color: 'primary.main',
                  mb: 2,
                  animation: 'pulse 1.5s infinite',
                }}
              />
              <Typography
                variant='h2'
                component='h1'
                gutterBottom
                fontWeight='bold'
                sx={{
                  background: 'linear-gradient(45deg, #3f51b5, #f50057)',
                  backgroundClip: 'text',
                  textFillColor: 'transparent',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                {t('sponsor')}
              </Typography>
              <Typography
                variant='h6'
                color='text.secondary'
                sx={{maxWidth: '800px', mx: 'auto', mb: 4}}
              >
                {t('sponsorDescription')}
              </Typography>
              <Divider sx={{width: '100px', mx: 'auto', mb: 6}} />
            </Box>
          </Fade>

          <Grid container spacing={5} alignItems='flex-start'>
            <Grid item xs={12} md={6}>
              <Zoom triggerOnce>
                <Paper
                  elevation={3}
                  sx={{
                    p: 4,
                    borderRadius: 4,
                    overflow: 'hidden',
                    boxShadow: '0 8px 40px rgba(0,0,0,0.12)',
                    background: 'white',
                    position: 'relative',
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '6px',
                      background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    }}
                  />

                  <Typography
                    variant='h5'
                    component='h2'
                    gutterBottom
                    fontWeight='bold'
                    sx={{mb: 3}}
                  >
                    {t('sponsorAmount')}
                  </Typography>

                  <Box sx={{mb: 4}}>
                    <Grid container spacing={2} alignItems='center'>
                      <Grid item xs={12} sm={7}>
                        <TextField
                          fullWidth
                          label={t('enterAmount')}
                          variant='outlined'
                          type='number'
                          value={amount}
                          onChange={e => setAmount(Number(e.target.value))}
                          InputProps={{
                            endAdornment: (
                              <Typography color='textSecondary' sx={{pl: 1}}>
                                USDT
                              </Typography>
                            ),
                            inputProps: {min: 1, step: 1},
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '&:hover fieldset': {
                                borderColor: 'primary.main',
                              },
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={5}>
                        <Button
                          fullWidth
                          variant='contained'
                          color='primary'
                          size='large'
                          onClick={handleDonation}
                          disabled={loading || !amount || amount < 1}
                          sx={{
                            py: 1.8,
                            bgcolor: 'primary.main',
                            '&:hover': {
                              bgcolor: 'primary.dark',
                              transform: 'translateY(-2px)',
                              boxShadow: '0 6px 20px rgba(63, 81, 181, 0.4)',
                            },
                            transition: 'all 0.3s ease',
                          }}
                        >
                          {loading ? (
                            <CircularProgress size={24} color='inherit' />
                          ) : (
                            t('sponsorButtonText', {amount})
                          )}
                        </Button>
                      </Grid>
                    </Grid>
                  </Box>

                  {error && (
                    <Alert severity='error' sx={{mt: 2, borderRadius: 2}}>
                      {error}
                    </Alert>
                  )}

                  <Box sx={{mt: 3}}>
                    <Typography
                      variant='body2'
                      color='textSecondary'
                      align='center'
                      sx={{fontStyle: 'italic'}}
                    >
                      {t('sponsorSecurePayment')}
                    </Typography>

                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        mt: 2.5,
                        gap: themeInstance.spacing(1.5),
                        flexWrap: 'wrap',
                      }}
                    >
                      {[5, 10, 50, 100].map(value => (
                        <Button
                          key={value}
                          size='small'
                          variant='outlined'
                          onClick={() => setAmount(value)}
                          sx={{
                            borderRadius: '16px',
                            borderColor: alpha(
                              themeInstance.palette.divider,
                              0.7,
                            ),
                            color: themeInstance.palette.text.secondary,
                            textTransform: 'none',
                            padding: '4px 16px',
                            minWidth: '80px',
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                              borderColor: themeInstance.palette.primary.main,
                              backgroundColor: alpha(
                                themeInstance.palette.primary.main,
                                0.08,
                              ),
                              color: themeInstance.palette.primary.dark,
                              transform: 'translateY(-1px)',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                            },
                          }}
                        >
                          {value} USDT
                        </Button>
                      ))}
                    </Box>
                  </Box>
                </Paper>
              </Zoom>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography
                variant='h5'
                gutterBottom
                fontWeight='bold'
                sx={{mb: 3}}
              >
                {t('supportUsageTitle')}
              </Typography>

              <Stack spacing={2}>
                {usagePurposes.map((purpose, index) => (
                  <Fade key={index} delay={index * 100} triggerOnce>
                    <Card
                      elevation={1}
                      sx={{
                        borderRadius: 3,
                        transition: 'transform 0.3s, box-shadow 0.3s',
                        '&:hover': {
                          transform: 'translateY(-5px)',
                          boxShadow: '0 12px 20px rgba(0,0,0,0.1)',
                        },
                      }}
                    >
                      <CardContent sx={{p: 3}}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                          }}
                        >
                          <Box
                            sx={{
                              borderRadius: '50%',
                              p: 1.5,
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                            }}
                          >
                            {purpose.icon}
                          </Box>
                          <Box>
                            <Typography
                              variant='h6'
                              fontWeight='bold'
                              color='primary'
                            >
                              {purpose.title}
                            </Typography>
                            <Typography variant='body1'>
                              {purpose.description}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Fade>
                ))}
              </Stack>

              <Box
                sx={{
                  mt: 4,
                  p: 3,
                  bgcolor: alpha('#e3f2fd', 0.5),
                  borderRadius: 3,
                }}
              >
                <Typography variant='h6' gutterBottom fontWeight='medium'>
                  {t('thankSponsors')}
                </Typography>
                <Typography variant='body2' paragraph>
                  {t('sponsorNameList')}
                </Typography>
                <Typography
                  variant='body2'
                  sx={{fontStyle: 'italic', color: 'text.secondary'}}
                >
                  {t('sponsorQuote')}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Footer />

      <Snackbar
        open={success}
        autoHideDuration={3000}
        onClose={() => setSuccess(false)}
        anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
      >
        <Alert
          severity='success'
          variant='filled'
          sx={{
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
          }}
        >
          {t('sponsorThankYou')}
        </Alert>
      </Snackbar>

      <style>{`
        @keyframes pulse {
          0% {
            transform: scale(1);
            opacity: 1;
          }
          50% {
            transform: scale(1.1);
            opacity: 0.8;
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }
      `}</style>
    </ThemeProvider>
  );
}
